import React from 'react';

import { useSelector } from 'react-redux';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import Unauthorized from 'scenes/Unauthorized/Unauthorized';

import { MenuNode } from 'models/MenuNode';
import { User } from 'models/User';

import {
  hasRequiredUserTypes,
  hasRoutePermissions,
  userHasMenuAccessForPath,
} from 'utils/AuthUtils';

import { MenuType } from '../../config/Config';

type PrivateRouteProps = {
  outlet: JSX.Element;
  permissionsRequired?: string | string[];
  /** Si se define, el userType del usuario debe estar en la lista. */
  userTypesRequired?: string | string[];
  /**
   * Si true, exige que el path esté presente en el árbol de menús del usuario
   * (mismo criterio que el menú lateral).
   */
  requireMenuAccess?: boolean;
  /**
   * Si true, tener el path en el árbol de menús también cumple
   * `userTypesRequired` (permite delegar la vista a usuarios regulares).
   */
  allowMenuAccess?: boolean;
  type?: MenuType;
};

function PrivateRoute({
  outlet,
  permissionsRequired = [],
  userTypesRequired = [],
  requireMenuAccess = false,
  allowMenuAccess = false,
  type = MenuType.STANDARD_MENU,
}: PrivateRouteProps) {
  const user: User = useSelector((state: any) => state.auth);
  const params = useParams();
  const location = useLocation();

  const pathname = location.pathname || '';
  const isAuthenticated = user?.id !== 0;
  const hasPermissions = hasRoutePermissions(permissionsRequired, user);
  const hasUserType =
    hasRequiredUserTypes(userTypesRequired, user) ||
    (allowMenuAccess && userHasMenuAccessForPath(user, pathname));
  const hasMenuAccess =
    !requireMenuAccess || userHasMenuAccessForPath(user, pathname);

  if (isAuthenticated && hasPermissions && hasUserType && hasMenuAccess) {
    if (
      type &&
      [
        MenuType.IFRAME_VIEWER,
        MenuType.POWER_BI_REPORT,
        MenuType.PDF_VIEWER,
        MenuType.OFFICE_VIEWER,
      ].includes(type)
    ) {
      const { key } = params;

      const findNodeByCodeAndType = (
        nodes: MenuNode[] = [],
        code: string | undefined,
        menuType?: MenuType
      ): MenuNode | undefined => {
        if (!code) return undefined;

        for (const node of nodes) {
          if (
            node.code === code &&
            String(node.menuTypeId) === String(menuType)
          ) {
            return node;
          }

          if (node.children && node.children.length > 0) {
            const found = findNodeByCodeAndType(node.children, code, menuType);
            if (found) return found;
          }
        }
        return undefined;
      };

      const availableMenuData = findNodeByCodeAndType(
        (user?.menus as MenuNode[]) || [],
        key,
        type
      );

      if (!availableMenuData) {
        return <Unauthorized />;
      }
    }

    return outlet;
  } else if (isAuthenticated) {
    return <Unauthorized />;
  }

  return <Navigate to="/login" />;
}

export default PrivateRoute;
